/**
 * ReviewPanel — line-by-line transaction review.
 *
 * Shows ONE transaction at a time as a big card. User classifies with
 * big pill buttons (Category / Cost centre / Team), then presses
 *   • KEEP     → marks reviewed=true, advances
 *   • EXCLUDE  → HARD-DELETES the row so it never affects totals, advances
 *   • SKIP     → advances without saving
 *   • Back     → previous card
 *
 * Keyboard shortcuts:
 *   ← / →     prev / next
 *   K         Keep
 *   X         Exclude
 *   1…7       category quick-pick
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, ArrowRight, Check, Trash2, SkipForward, Sparkles, Loader2, CheckCircle2, Plus, RotateCcw,
  FileText, Building,
  Hotel, Fuel, Gift, Users, Building2, Truck, TrendingUp, Landmark, HeartPulse, CreditCard, PartyPopper, Stethoscope, Laptop, Package, Briefcase, ParkingCircle, Key, Share2, Crown, Shield, DollarSign, User, Undo2, HelpCircle, Repeat,
} from "lucide-react";
import { toast } from "sonner";
import {
  listTransactions, updateTransaction, deleteTransaction, money,
} from "@/lib/operations/api";
import ManualEntryDialog from "@/pages/operations/ManualEntryDialog";

const CAT_ICON = {
  hotels: Hotel, gas: Fuel, incentives: Gift, staff: Users,
  office: Building2, travel: Truck, adherence: HeartPulse,
  banking: CreditCard, entertainment: PartyPopper, clinical: Stethoscope,
  technology: Laptop, supplies: Package, consultant: Briefcase,
  parking: ParkingCircle, rent: Key, referrals: Share2, management: Crown,
  insurance: Shield, income: TrendingUp, personal: User, refunds: Undo2, subscriptions: Repeat, unknown: HelpCircle, other: DollarSign,
};

// Fixed sponsor roster — matches the pill-button set on the Upload tab
// so operators see the same options in the same order across screens.
const SPONSOR_COMPANIES = ["420", "HepCURE", "Christian", "Joséphine", "George", "Racquel"];

export default function ReviewPanel({
  config, onChange, statement, onClearStatement, sponsor = "",
  onlyPending, setOnlyPending,
}) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showManual, setShowManual] = useState(false);
  // Optional category filter — when set, both card and table views only
  // show rows in that category. Independent of the pending/reviewed state.
  const [filterCategory, setFilterCategory] = useState("");
  // Optional company / sponsor filter (independent of category).
  // Company filter now comes from the global sponsor row in
  // Operations.jsx — kept as a local mirror so the existing filter
  // logic works untouched.
  const filterCompany = sponsor;
  useEffect(() => { curIdRef.current = null; setIdx(0); }, [sponsor]);
  // Optional flag filter — when true, only show is_loan rows. Used by
  // the "Loans" quick tab so operators can jump straight to reimbursement
  // items without hunting through the queue.
  const [filterIsLoan, setFilterIsLoan] = useState(false);
  // Optional source filter — "" (all) | "import" | "manual". Independent
  // of category / company / pending. Lets the user isolate manual PayPal-
  // style entries from bank/CC statement imports.
  const [filterSource, setFilterSource] = useState("");
  // Ids that were just kept but should stay in the queue so pressing Back
  // returns to them for edits. Populated by goBack after a Keep.
  const [keepVisibleIds, setKeepVisibleIds] = useState(() => new Set());
  // The most recent id that was Kept — Back pulls it back into the queue.
  const [lastKeptId, setLastKeptId] = useState(null);
  // The id of the card currently on screen. We track this alongside `idx`
  // so that queue re-sorts (e.g. an Unknown pick floating the row to the
  // top) don't teleport the operator to a different row. Whenever the
  // queue reorders, we re-align idx to the new position of this id.
  const curIdRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listTransactions(
        statement?.id ? { statement_id: statement.id } : {},
      );
      setTxns(list);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [statement?.id]);
  useEffect(() => { reload(); }, [reload]);
  // Reset the card index whenever the statement filter changes so we don't
  // start mid-way through the previous list.
  useEffect(() => { curIdRef.current = null; setIdx(0); }, [statement?.id]);

  // Any explicit filter change should also drop the pin so the operator
  // lands at position 0 of the newly-filtered queue instead of being
  // teleported back to the row they were on before the filter.
  useEffect(() => {
    curIdRef.current = null;
    setIdx(0);
  }, [filterCategory, filterSource, filterIsLoan, onlyPending]);

  // Base list optionally scoped to a single category + company + source
  const scopedTxns = useMemo(
    () => txns.filter((t) => {
      if (filterCategory && (t.category || "") !== filterCategory) return false;
      if (filterCompany && (t.company || "") !== filterCompany) return false;
      if (filterIsLoan && !t.is_loan) return false;
      if (filterSource) {
        const src = t.source || "import";
        if (src !== filterSource) return false;
      }
      return true;
    }),
    [txns, filterCategory, filterCompany, filterIsLoan, filterSource],
  );

  // Filtered queue for the card view. Unknown rows float to the top so
  // operators triage them first (they carry no signal yet).
  const queue = useMemo(() => {
    const base = onlyPending
      ? scopedTxns.filter((t) => !t.reviewed || keepVisibleIds.has(t.id))
      : scopedTxns;
    return [...base].sort((a, b) => {
      const aUnknown = (a.category || "") === "unknown" ? 0 : 1;
      const bUnknown = (b.category || "") === "unknown" ? 0 : 1;
      return aUnknown - bUnknown;
    });
  }, [scopedTxns, onlyPending, keepVisibleIds]);
  const cur = queue[idx] || null;

  // Keep `idx` locked onto the same transaction (by id) even when the
  // queue re-sorts under it — e.g. tapping "Unknown" makes the current
  // row float to the top, which would otherwise silently teleport the
  // operator to a different row.
  useEffect(() => {
    if (!cur) {
      curIdRef.current = null;
      return;
    }
    if (curIdRef.current && curIdRef.current !== cur.id) {
      const newPos = queue.findIndex((t) => t.id === curIdRef.current);
      if (newPos >= 0 && newPos !== idx) {
        setIdx(newPos);
        return;
      }
    }
    curIdRef.current = cur.id;
  }, [cur, queue, idx]);

  // Clamp index when the queue shrinks (delete/keep flow)
  useEffect(() => {
    if (idx > queue.length - 1) setIdx(Math.max(0, queue.length - 1));
  }, [queue.length, idx]);

  const saveField = async (patch, silent = false) => {
    if (!cur) return null;
    setBusy(true);
    try {
      const saved = await updateTransaction(cur.id, patch);
      setTxns((cur2) => cur2.map((t) => (t.id === saved.id ? saved : t)));
      onChange?.();
      return saved;
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setBusy(false);
    }
  };

  // Same, but takes an explicit id (used by the table view).
  const saveById = async (id, patch) => {
    try {
      const saved = await updateTransaction(id, patch);
      setTxns((cur2) => cur2.map((t) => (t.id === saved.id ? saved : t)));
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deleteById = async (id) => {
    if (!window.confirm("Delete this transaction? It cannot be recovered.")) return;
    try {
      await deleteTransaction(id);
      setTxns((cur2) => cur2.filter((t) => t.id !== id));
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const advance = () => {
    // Break the pin so the sync effect picks up the new card's id.
    curIdRef.current = null;
    setIdx((i) => Math.min(queue.length, i + 1));
  };

  // Back button — if the user just pressed Keep, pull that row back into
  // the queue so they can fix a mistake. Otherwise, standard idx-1.
  const goBack = () => {
    // Break the pin so the sync effect doesn't yank us back to the row
    // we're trying to leave.
    curIdRef.current = null;
    if (lastKeptId) {
      const targetId = lastKeptId;
      setLastKeptId(null);
      setKeepVisibleIds((s) => {
        const next = new Set(s);
        next.add(targetId);
        return next;
      });
      // After the queue re-computes, jump to the just-kept item's position.
      // Use a microtask so state updates settle first.
      setTimeout(() => {
        setIdx(() => {
          const list = onlyPending
            ? txns.filter((t) => !t.reviewed || t.id === targetId || keepVisibleIds.has(t.id))
            : txns;
          const pos = list.findIndex((t) => t.id === targetId);
          return pos >= 0 ? pos : 0;
        });
      }, 0);
      return;
    }
    setIdx((i) => Math.max(0, i - 1));
  };

  const onKeep = async () => {
    if (!cur) return;
    if (!cur.category) {
      toast.error("Set a category before keeping this row.");
      return;
    }
    // 'personal' and 'unknown' rows are out-of-scope for cost-centre
    // accounting — the operator hasn't figured out what the row is yet
    // (unknown) or it's not the org's expense (personal).
    const requiresCostCentre = !(["personal", "unknown"].includes(cur.category));
    if (requiresCostCentre && !cur.cost_centre) {
      toast.error("Set a category and cost centre before keeping this row.");
      return;
    }
    const savedId = cur.id;
    const saved = await saveField({ reviewed: true });
    if (saved) {
      // Remember this id so Back can revisit it. Also drop it from the
      // grace set (if it was there from a previous back-and-forth) so a
      // fresh Keep behaves like the normal flow.
      setLastKeptId(savedId);
      setKeepVisibleIds((s) => {
        if (!s.has(savedId)) return s;
        const next = new Set(s);
        next.delete(savedId);
        return next;
      });
      advance();
    }
  };

  // Reopen a previously-kept row for reclassification. Sets reviewed=false
  // so it goes back into the pending queue and stays visible on the card
  // while the operator picks a new category / cost centre.
  const onReopen = async () => {
    if (!cur) return;
    const savedId = cur.id;
    const saved = await saveField({ reviewed: false });
    if (saved) {
      // Keep the id visible on the current card until the operator moves
      // on — otherwise the pending-only filter would immediately hide it
      // even though we WANT it in the queue.
      setKeepVisibleIds((s) => new Set(s).add(savedId));
      toast.success("Sent back to review — pick a new category.");
    }
  };

  const onExclude = async () => {
    if (!cur) return;
    if (!window.confirm("Exclude this transaction? It will be permanently deleted.")) return;
    setBusy(true);
    try {
      await deleteTransaction(cur.id);
      setTxns((cur2) => cur2.filter((t) => t.id !== cur.id));
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  // ─── Keyboard shortcuts ────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (showAll) return;
      if (["INPUT", "SELECT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (e.key === "ArrowRight") { e.preventDefault(); advance(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goBack(); }
      else if (e.key === "k" || e.key === "K") { e.preventDefault(); onKeep(); }
      else if (e.key === "x" || e.key === "X") { e.preventDefault(); onExclude(); }
      else if (e.key >= "1" && e.key <= "9" && config?.categories) {
        const i = parseInt(e.key, 10) - 1;
        const c = config.categories[i];
        if (c && cur) saveField({ category: c.key }, true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cur, showAll, queue.length]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading transactions…
      </div>
    );
  }
  if (txns.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/60 p-10 text-center">
        <p className="text-brand-navy font-bold text-lg">No transactions yet</p>
        <p className="mt-1 text-sm text-slate-500">Upload a bank statement to start reviewing.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {statement && (
        <div
          className="rounded-2xl bg-brand-mist/40 border border-brand-teal/30 px-4 py-3 flex items-center gap-3 flex-wrap"
          data-testid="ops-review-statement-banner"
        >
          <FileText className="h-5 w-5 text-brand-teal shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-brand-teal font-semibold">
              Reviewing statement
            </p>
            <p className="text-sm font-semibold text-brand-navy truncate">
              {statement.filename}
              <span className="ml-2 text-xs text-slate-500 font-normal">
                {statement.row_count} transactions
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClearStatement}
            className="text-xs font-semibold text-brand-navy hover:underline shrink-0"
            data-testid="ops-review-clear-statement"
          >
            Show all statements
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Category filter — pill buttons for parity with the sponsor
              filter. "All" clears the filter. Full-width so it wraps
              cleanly on narrow screens. */}
          <div className="w-full" data-testid="ops-review-category-toggle">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
              Category
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: "", label: "All" },
                ...config.categories.map((c) => ({ key: c.key, label: c.label })),
              ].map(({ key, label }) => {
                const active = filterCategory === key;
                return (
                  <button
                    key={key || "__all__"}
                    type="button"
                    onClick={() => { setFilterCategory(key); setIdx(0); }}
                    className={`inline-flex items-center justify-center h-8 px-3 rounded-lg border text-xs font-semibold transition min-w-0 ${
                      active
                        ? "bg-brand-navy text-white border-brand-navy shadow-sm"
                        : "bg-white text-brand-navy/80 border-slate-200 hover:border-brand-navy/40 hover:text-brand-navy"
                    }`}
                    data-testid={`ops-review-category-${key || "all"}`}
                  >
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Company / sponsor filter — now handled by the global sponsor
              row in Operations.jsx, so this panel doesn't render its own. */}
          {/* Source filter — Statement (import) vs Manual entry */}
          <div className="inline-flex items-center rounded-full bg-white border border-slate-200 p-0.5" data-testid="ops-review-source-toggle">
            {[
              { key: "", label: "All" },
              { key: "import", label: "Statement" },
              { key: "manual", label: "Manual" },
            ].map((opt) => {
              const active = filterSource === opt.key;
              return (
                <button
                  key={opt.key || "all"}
                  type="button"
                  onClick={() => { setFilterSource(opt.key); setIdx(0); }}
                  className={`h-7 px-3 rounded-full text-xs font-semibold transition ${
                    active
                      ? "bg-brand-navy text-white shadow-sm"
                      : "text-brand-navy/70 hover:text-brand-navy"
                  }`}
                  data-testid={`ops-review-source-${opt.key || "all"}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <span className="text-sm text-slate-500 tabular-nums" data-testid="ops-review-progress">
            {queue.length === 0 ? "0 of 0" : `${Math.min(idx + 1, queue.length)} of ${queue.length}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowManual(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white border border-brand-teal/50 text-brand-teal hover:bg-brand-teal hover:text-white font-semibold text-xs transition"
            data-testid="ops-add-manual"
          >
            <Plus className="h-3.5 w-3.5" /> Manual entry
          </button>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-sm font-semibold text-brand-teal hover:underline"
            data-testid="ops-review-toggle-table"
          >
            {showAll ? "Card view" : "All as table"}
          </button>
        </div>
      </div>

      {showAll
        ? <AllTable txns={scopedTxns} config={config} onUpdate={saveById} onDelete={deleteById} />
        : (
          queue.length === 0 || !cur
            ? (
              <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto" />
                <p className="mt-3 text-brand-navy font-bold text-lg">All caught up</p>
                <p className="mt-1 text-sm text-slate-500">Every transaction has been reviewed.</p>
                {onlyPending && (
                  <button
                    type="button"
                    onClick={() => { setOnlyPending(false); setIdx(0); }}
                    className="mt-4 text-sm font-semibold text-brand-teal hover:underline"
                  >
                    Show reviewed transactions
                  </button>
                )}
              </div>
            )
            : (
              <TransactionCard
                cur={cur}
                config={config}
                busy={busy}
                onField={saveField}
                onKeep={onKeep}
                onExclude={onExclude}
                onBack={goBack}
                onSkip={advance}
                onReopen={onReopen}
              />
            )
        )}

      {showManual && (
        <ManualEntryDialog
          config={config}
          onClose={() => setShowManual(false)}
          onCreated={(doc) => {
            setShowManual(false);
            setTxns((cur2) => [doc, ...cur2]);
            onChange?.();
          }}
        />
      )}
    </div>
  );
}

// ─── Card view ────────────────────────────────────────────────────────────
function TransactionCard({ cur, config, busy, onField, onKeep, onExclude, onBack, onSkip, onReopen }) {
  const isIncome = Number(cur.amount) > 0;
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-8" data-testid={`ops-review-card-${cur.id}`}>
      {/* Header — vertical stack so the amount always has room */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <span>{cur.date}</span>
          <span aria-hidden>·</span>
          <span className={isIncome ? "text-emerald-600" : "text-slate-500"}>
            {isIncome ? "Income" : "Expense"}
          </span>
          {cur.source === "manual" && (
            <span
              className="ml-1 inline-flex items-center px-1.5 h-4 rounded bg-brand-teal/10 text-brand-teal text-[10px] font-bold tracking-wide"
              data-testid="ops-review-card-manual-pill"
              title="Manually entered (not from a statement)"
            >
              MANUAL
            </span>
          )}
        </div>
        <p
          className={`text-3xl sm:text-5xl font-bold tabular-nums leading-none ${
            isIncome ? "text-emerald-600" : "text-brand-navy"
          }`}
        >
          {isIncome ? "+" : "−"}{money(cur.amount)}
        </p>
        <h3 className="text-base sm:text-lg font-semibold text-brand-navy break-words leading-snug">
          {cur.description}
        </h3>
        {cur.reason && (
          <p className="text-sm text-slate-500 leading-relaxed pt-1">
            <Sparkles className="inline h-3.5 w-3.5 -mt-0.5 mr-1.5 text-brand-teal" />
            <span className="font-semibold text-slate-600">AI:</span>{" "}
            {cur.reason}{" "}
            <span className="text-slate-400 whitespace-nowrap">
              ({Math.round((cur.confidence || 0) * 100)}%)
            </span>
          </p>
        )}
      </div>

      {/* Divider */}
      <div className="mt-6 border-t border-slate-100" />

      {/* Category */}
      <div className="mt-5">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Category</p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
          {config.categories.map((c) => {
            const Icon = CAT_ICON[c.key] || DollarSign;
            const active = cur.category === c.key;
            return (
              <button
                key={c.key}
                type="button"
                disabled={busy}
                onClick={() => onField(
                  // Personal purchases are out-of-scope for the org, and
                  // Unknown rows haven't been triaged yet — either way
                  // any previously-set cost centre is cleared.
                  ["personal", "unknown"].includes(c.key)
                    ? { category: c.key, cost_centre: null }
                    : { category: c.key },
                  true,
                )}
                className={`inline-flex items-center justify-start gap-1.5 h-8 pl-2.5 pr-2 rounded-full text-xs font-semibold border transition min-w-0 ${
                  active
                    ? "bg-brand-navy text-white border-brand-navy"
                    : "bg-slate-50 text-brand-navy border-slate-200 hover:border-brand-navy/40 hover:bg-white"
                }`}
                data-testid={`ops-cat-${c.key}`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{c.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Subcategory — only rendered when the current category has a
          subcategory registry (e.g. Technology → Hosting / Development /
          Video). Same pill styling as the main category picker. */}
      {(config.subcategories?.[cur.category] || []).length > 0 && (
        <div className="mt-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
            {config.categories.find((c) => c.key === cur.category)?.label} · subcategory
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="ops-review-subcategory">
            {config.subcategories[cur.category].map((sc) => {
              const active = cur.subcategory === sc.key;
              return (
                <button
                  key={sc.key}
                  type="button"
                  disabled={busy}
                  onClick={() => onField(
                    { subcategory: active ? null : sc.key },
                    true,
                  )}
                  className={`inline-flex items-center justify-center h-8 px-3 rounded-full text-xs font-semibold border transition min-w-0 ${
                    active
                      ? "bg-brand-navy text-white border-brand-navy"
                      : "bg-slate-50 text-brand-navy border-slate-200 hover:border-brand-navy/40 hover:bg-white"
                  }`}
                  data-testid={`ops-sub-${cur.category}-${sc.key}`}
                >
                  {sc.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Loan toggle — flag any row (regardless of category) as a loan
          disbursement or repayment so it feeds the reimbursement total. */}
      <div className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
            <Landmark className="h-3 w-3" /> Is this a loan?
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Toggle on for loan disbursements received or loan repayments paid — it counts toward the reimbursement tracker.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!cur.is_loan}
          disabled={busy}
          onClick={() => onField({ is_loan: !cur.is_loan }, true)}
          className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition ${
            cur.is_loan ? "bg-brand-teal" : "bg-slate-300"
          }`}
          data-testid="ops-review-is-loan-toggle"
        >
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition transform ${
              cur.is_loan ? "translate-x-5" : "translate-x-0.5"
            } my-0.5`}
          />
        </button>
      </div>

      {/* Cost centre — hidden entirely for 'personal' and 'unknown' rows
          since both are out-of-scope until further triage. */}
      {!["personal", "unknown"].includes(cur.category) && (
      <div className="mt-5">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Cost centre</p>
        <div className="grid grid-cols-3 gap-1.5">
          {config.cost_centres.map((cc) => {
            const active = cur.cost_centre === cc.key;
            return (
              <button
                key={cc.key}
                type="button"
                disabled={busy}
                onClick={() => onField({ cost_centre: cc.key }, true)}
                className={`inline-flex items-center justify-center h-8 px-2 rounded-full text-xs font-semibold border transition min-w-0 ${
                  active
                    ? "bg-brand-teal text-white border-brand-teal"
                    : "bg-slate-50 text-brand-navy border-slate-200 hover:border-brand-teal/40 hover:bg-white"
                }`}
                data-testid={`ops-cc-${cc.key}`}
              >
                <span className="truncate">{cc.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Actions — all four on one row on mobile */}
      <div className="mt-8 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="h-11 w-11 rounded-full text-slate-600 hover:bg-slate-100 flex items-center justify-center transition shrink-0"
          aria-label="Previous transaction"
          title="Back"
          data-testid="ops-review-back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={onSkip}
          disabled={busy}
          className="h-11 w-11 rounded-full bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 flex items-center justify-center transition disabled:opacity-50 shrink-0"
          aria-label="Skip"
          title="Skip"
          data-testid="ops-review-skip"
        >
          <SkipForward className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={onExclude}
          disabled={busy}
          className="h-11 w-11 rounded-full bg-white text-red-600 border border-red-200 hover:bg-red-50 flex items-center justify-center transition disabled:opacity-50 shrink-0"
          aria-label="Exclude"
          title="Exclude (delete)"
          data-testid="ops-review-exclude"
        >
          <Trash2 className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={onKeep}
          disabled={busy}
          className="flex-1 h-11 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold flex items-center justify-center gap-2 shadow-sm transition disabled:opacity-50"
          data-testid="ops-review-keep"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Keep <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {/* Send-back-to-review — only for already-kept rows so the operator
          can un-review and reclassify from Personal/Unknown back to a
          real business category without needing to hunt through the DB. */}
      {cur.reviewed && (
        <button
          type="button"
          onClick={onReopen}
          disabled={busy}
          className="mt-3 w-full h-10 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-sm font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50"
          data-testid="ops-review-reopen"
        >
          <RotateCcw className="h-4 w-4" />
          Send back to review (reclassify)
        </button>
      )}

      <p className="mt-4 text-xs text-slate-400 text-center hidden sm:block">
        Shortcuts: <kbd className="px-1 bg-slate-100 rounded">←</kbd>/<kbd className="px-1 bg-slate-100 rounded">→</kbd> navigate · <kbd className="px-1 bg-slate-100 rounded">K</kbd> keep · <kbd className="px-1 bg-slate-100 rounded">X</kbd> exclude
      </p>
    </div>
  );
}

// ─── Compact table view (for bulk editing after initial review) ──────────
function AllTable({ txns, config, onUpdate, onDelete }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="ops-review-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-3 w-28">Date</th>
              <th className="px-3 py-3">Description</th>
              <th className="px-3 py-3 w-28 text-right">Amount</th>
              <th className="px-3 py-3 w-32">Company</th>
              <th className="px-3 py-3 w-36">Category</th>
              <th className="px-3 py-3 w-32">Cost centre</th>
              <th className="px-3 py-3 w-20">Status</th>
              <th className="px-3 py-3 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {txns.map((t) => (
              <tr key={t.id}>
                <td className="px-3 py-3 tabular-nums text-slate-600">{t.date}</td>
                <td className="px-3 py-3 text-brand-navy font-medium max-w-[280px] truncate" title={t.description}>
                  <span className="inline-flex items-center gap-1.5">
                    {t.source === "manual" && (
                      <span
                        className="inline-flex items-center px-1.5 h-4 rounded bg-brand-teal/10 text-brand-teal text-[9px] font-bold tracking-wide shrink-0"
                        title="Manually entered (not from a statement)"
                      >
                        M
                      </span>
                    )}
                    <span className="truncate">{t.description}</span>
                  </span>
                </td>
                <td className="px-3 py-3 text-right tabular-nums font-semibold text-brand-navy">{money(t.amount)}</td>
                <td className="px-3 py-3 text-slate-600 text-xs truncate max-w-[140px]" title={t.company || ""}>
                  {t.company || <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-3">
                  <select
                    value={t.category || ""}
                    onChange={(e) => onUpdate(t.id, { category: e.target.value })}
                    className="w-full h-8 rounded-md border border-slate-200 bg-white px-2 text-sm"
                  >
                    {config.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-3">
                  <select
                    value={t.cost_centre || ""}
                    onChange={(e) => onUpdate(t.id, { cost_centre: e.target.value })}
                    className="w-full h-8 rounded-md border border-slate-200 bg-white px-2 text-sm"
                  >
                    <option value="">—</option>
                    {config.cost_centres.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-3">
                  {t.reviewed
                    ? <span className="text-xs font-semibold text-emerald-700">Reviewed</span>
                    : <span className="text-xs font-semibold text-amber-700">Pending</span>}
                </td>
                <td className="px-3 py-3">
                  <button
                    type="button"
                    onClick={() => onDelete(t.id)}
                    className="h-7 w-7 rounded-md text-red-500 hover:bg-red-50 flex items-center justify-center"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
